/**
 * @documental
 * @export Passwordbox
 */
import { forwardRef, useCallback, useLayoutEffect, useRef } from "react";

import { withSubComponents } from "../../utilities/withSubComponents";
import { RefComponent } from "../RefComponent";
import { TextboxProps } from "../Textbox";
import { Input } from "../Textbox/Input";
import { Placeholder } from "../Textbox/Placeholder";
import { Field } from "./Field";

export interface PasswordboxProps extends TextboxProps {
  /**
   * Indicates whether the field is required.
   */
  readonly required?: boolean;
}

/**
 * The `Passwordbox` component sub components.
 */
export interface PasswordboxSubComponents {
  /**
   * A React Hook Form compatible Passwordbox Field component. For more information, see the documentation for
   * the <a href="https://react-hook-form.com/api">React Hook Form API</a>.
   */
  readonly Field: typeof Field;

  /**
   * A placeholder component for the Passwordbox which can be displayed while loading.
   */
  readonly Placeholder: typeof Placeholder;
}

/**
 * An input component which allows the user to enter a password.
 */
export const Passwordbox = withSubComponents<RefComponent<HTMLInputElement, PasswordboxProps>, PasswordboxSubComponents>(
  {
    Field,
    Placeholder,
  },
  forwardRef<HTMLInputElement, PasswordboxProps>(function Passwordbox(props, ref) {
    const { defaultValue = null } = props;
    const { value = defaultValue } = props;

    const elementRef = useRef<HTMLInputElement | null>(null);

    const refHandler = useCallback(
      (element: HTMLInputElement | null) => {
        elementRef.current = element;
        element?.removeAttribute("value");

        if (ref) {
          if (typeof ref === "function") {
            ref(element);
          } else {
            ref.current = element;
          }
        }
      },
      [ref],
    );

    useLayoutEffect(() => {
      setTimeout(() => {
        elementRef.current?.removeAttribute("value");
      });
    }, [value]);

    return <Input {...props} ref={refHandler} type="password" />;
  }),
);
